import clsx from 'clsx'
import { useRouter } from 'next/router'
import qs from 'query-string'
import React from 'react'
import type { TPhase, TPhaseGroup } from 'types/classAndPhases'
import type {
  TGeneralToolkit,
  TGeneralToolkitSplitBahanAjar,
  ToolkitVideosFilter,
  TToolkitType,
} from 'types/toolkit'
import shallow from 'zustand/shallow'

import { Badge } from '@wartek-id/badge'
import { Button } from '@wartek-id/button'
import { Checkbox, CheckboxGroup } from '@wartek-id/checkbox'
import { Text } from '@wartek-id/text'
import { analyticsEventHandler } from '@wartek-id/toolbox-analytics-event-handler'

import { parseQuery } from 'utils/router'
import { getSession } from 'utils/session'
import { track } from 'utils/tracker'

import { useToolkitAbTestContext } from 'apps/shared/ToolkitAbTest/context'

import { useFilterStore } from 'stores/filters'
import { useSavedSearchFilterStore } from 'stores/savedSearchFilter'
import { useTogglesStore } from 'stores/toggles'

import {
  GENERAL_TOOLKIT_LIST,
  GENERAL_TOOLKIT_SPLIT_BAHAN_AJAR_LIST,
  GENERAL_TOOLKIT_SPLIT_BAHAN_AJAR_TO_TOOLKIT_TYPE_MAPPING,
  GENERAL_TOOLKIT_TO_TOOLKIT_TYPE_MAPPING,
  TOOLKIT_TYPE_TO_GENERAL_TOOLKIT_MAPPING,
  TOOLKIT_TYPE_TO_GENERAL_TOOLKIT_SPLIT_BAHAN_AJAR_MAPPING,
} from 'data/toolkits'

import BannerSurvey from './BannerSurvey'
import { KeywordInput, ShowKeywordButton } from './KeywordInput'
import SelectedPhase from './SelectedPhase'
import SelectedSubject from './SelectedSubject'

import styles from './SearchSection.module.css'

interface TSearchSectionProps {
  setIsUsingCachedToolkits?: (boolean?) => void
  asFilterSheet?: boolean
}

const TOOLKIT_TYPES_WITH_KEYWORD: (
  | TGeneralToolkit
  | TGeneralToolkitSplitBahanAjar
)[] = ['modul_ajar', 'modul_bahan_ajar']

const SearchSection = ({
  setIsUsingCachedToolkits,
  asFilterSheet,
}: TSearchSectionProps) => {
  const timeout = React.useRef<NodeJS.Timeout>()
  const router = useRouter()
  const { query, pathname } = router
  const isInitialized = React.useRef(false)
  const { deviceId } = useToolkitAbTestContext() || {}
  const {
    phase: cachePhase,
    phaseGroup: cachePhaseGroup,
    modulBahanBukuSubject: cachedModulBahanBukuSubject,
    modulProjekSubject: cachedModulProjekSubject,
    modulProjekThemeKey: cachedModulProjekThemeKey,
    title: cacheTitle,
    videosFilter: cacheVideosFilter,
    setPhase,
    setPhaseGroup,
    setModulBahanBukuSubject,
    setModulProjekSubject,
    setModulProjekThemeKey,
    setTitle,
    setVideosFilter,
  } = useSavedSearchFilterStore(
    ({
      phase,
      phaseGroup,
      modulBahanBukuSubject,
      modulProjekSubject,
      modulProjekThemeKey,
      title,
      videosFilter,
      setPhase,
      setPhaseGroup,
      setModulBahanBukuSubject,
      setModulProjekSubject,
      setModulProjekThemeKey,
      setTitle,
      setVideosFilter,
    }) => ({
      phase,
      phaseGroup,
      modulBahanBukuSubject,
      modulProjekSubject,
      modulProjekThemeKey,
      title,
      videosFilter,
      setPhase,
      setPhaseGroup,
      setModulBahanBukuSubject,
      setModulProjekSubject,
      setModulProjekThemeKey,
      setTitle,
      setVideosFilter,
    }),
    shallow
  )

  const phase = parseQuery<TPhase[] | undefined>(query?.phase, 'array')
  const phaseGroup = parseQuery<TPhaseGroup | undefined>(query?.phaseGroup)
  const toolkitType = parseQuery<TToolkitType>(query?.toolkit_type)
  const subject = parseQuery<string>(query?.subject)
  const title = parseQuery<string>(query?.title)
  const queryKeyword = parseQuery<string>(query?.keyword)
  const themeKey = parseQuery<string>(query?.theme_key)
  const videosFilter = parseQuery<ToolkitVideosFilter[]>(
    query?.videos_filter,
    'array'
  )

  const getCurrentThemeValueByKey = useFilterStore(
    (state) => state.getCurrentThemeValueByKey,
    shallow
  )

  const currentThemeValue = getCurrentThemeValueByKey(themeKey)

  const { experimentStatus } = useToolkitAbTestContext()
  const isExperimentControl = experimentStatus === 'control'
  const isExperimentAlt2 = experimentStatus === 'alt-2'

  const [isKeywordActive, setIsKeywordActive] = React.useState<boolean>(false)
  const [keyword, setKeyword] = React.useState<string>(title || '')
  const inputRef = React.useRef<HTMLInputElement>(null)

  const [selectedVideosFilter, setSelectedVideosFilter] = React.useState<
    ToolkitVideosFilter[]
  >([])
  React.useEffect(() => {
    const currentQuery = { ...query }

    currentQuery.videos_filter = selectedVideosFilter

    if (selectedVideosFilter.length === 0) {
      delete currentQuery.videos_filter // TODO: change this later
    }

    router.replace(
      {
        pathname,
        query: { ...currentQuery },
      },
      undefined,
      { shallow: true }
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVideosFilter])

  const { enabled: analyticsEventHandlerEnabled, sendMoEngageEvent } =
    analyticsEventHandler()

  const toggles = useTogglesStore((state) => state.toggles)
  const isUseV2Theme = toggles?.['modul-projek-theme-v2']
  const isBannerSurveyShowed = toggles?.['banner-survey-search-page']
  const isABTestBahanAjarDiscoveryOn = toggles?.['ab-test-bahan-ajar-discovery']

  let generalToolkitList: {
    label: string
    name: TGeneralToolkit | TGeneralToolkitSplitBahanAjar
  }[] = GENERAL_TOOLKIT_LIST
  // Sorry for putting these unknowns.
  // Wrestling with typings for a temporary usage is a very unproductive usage of time.
  let generalToolkitToToolkitTypeMapping: unknown =
    GENERAL_TOOLKIT_TO_TOOLKIT_TYPE_MAPPING
  let toolkitTypeToGeneralToolkitMapping: unknown =
    TOOLKIT_TYPE_TO_GENERAL_TOOLKIT_MAPPING

  let defaultToolkitType: TGeneralToolkit | TGeneralToolkitSplitBahanAjar =
    'modul_bahan_ajar'

  let textSearchTitle = 'Ingin cari apa?'
  let textSearchActionButton = 'Cari Perangkat Ajar'

  if (isABTestBahanAjarDiscoveryOn && !isExperimentControl) {
    generalToolkitList = GENERAL_TOOLKIT_SPLIT_BAHAN_AJAR_LIST
    generalToolkitToToolkitTypeMapping =
      GENERAL_TOOLKIT_SPLIT_BAHAN_AJAR_TO_TOOLKIT_TYPE_MAPPING
    toolkitTypeToGeneralToolkitMapping =
      TOOLKIT_TYPE_TO_GENERAL_TOOLKIT_SPLIT_BAHAN_AJAR_MAPPING
    defaultToolkitType = 'modul_ajar'
    textSearchTitle = 'Mau cari apa?'
    textSearchActionButton = 'Tampilkan hasil'
  }

  React.useEffect(() => {
    if (isKeywordActive) inputRef.current.focus()
  }, [isKeywordActive])

  const [activeToolkitType, setActiveToolkitType] = React.useState<
    TGeneralToolkit | TGeneralToolkitSplitBahanAjar
  >(
    toolkitTypeToGeneralToolkitMapping[parseQuery<TToolkitType>(toolkitType)] ??
      defaultToolkitType
  )

  const isActiveToolkitTypeModulAjar =
    TOOLKIT_TYPES_WITH_KEYWORD.includes(activeToolkitType)
  const isActiveToolkitTypeBahanAjar = activeToolkitType === 'bahan_ajar'
  const isActiveToolkitTypeModulOrBahanAjar =
    isActiveToolkitTypeModulAjar || isActiveToolkitTypeBahanAjar
  const isActiveToolkitTypesContainModulProjek =
    activeToolkitType.includes('modul_projek')

  const isUseThemeValueAsSubject =
    isActiveToolkitTypesContainModulProjek && currentThemeValue && isUseV2Theme

  const selectedSubject = React.useMemo(() => {
    return isUseThemeValueAsSubject ? currentThemeValue : subject
  }, [subject, currentThemeValue, isUseThemeValueAsSubject])

  // If toolkit type not bahan ajar or experiment is not alt-2, then
  // proceed as usual (check toolkitType, phaseGroup, phase, and selected subject).
  // If toolkit type is bahan ajar, then
  // also check experiment type and whether keyword exists or not if experiment is alt-2,
  // but both experiments need to check if videos filter is selected or not.
  const isButtonDisabled =
    ((!isActiveToolkitTypeBahanAjar || !isExperimentAlt2) &&
      (!toolkitType ||
        !phaseGroup ||
        !phase ||
        phase?.length === 0 ||
        !selectedSubject)) ||
    (isActiveToolkitTypeBahanAjar &&
      ((isExperimentAlt2 ? !keyword : false) ||
        selectedVideosFilter.length === 0))

  const onToolkitTypeClicked = (
    toolkitType: TGeneralToolkit | TGeneralToolkitSplitBahanAjar
  ) => {
    setActiveToolkitType(toolkitType)
  }

  const handleFilterChange = (filterType: string) => {
    const pathQuery = {
      pathname: `/search/filter/${filterType}`,
      query: {
        ...query,
        from: pathname.replace('/', ''),
      },
    }
    return asFilterSheet ? router.replace(pathQuery) : router.push(pathQuery)
  }

  const trackClickSubmit = async () => {
    const session = await getSession()
    const userId = session?.user?.id || null
    const email = session?.user?.email || ''

    track({
      eventName: 'guru_core_pwa_event_tracker_v1',
      params: {
        timestamp: new Date().toISOString(),
        event_name: 'GURU_CORE_APP',
        activity_type: 'SEARCH_PERANGKAT_AJAR',
        device_id: deviceId,
        user_id: userId,
        attributes: [
          {
            key: 'user_agent',
            value: { string_value: window?.navigator?.userAgent ?? '' },
          },
          {
            key: 'teacher_email',
            value: { string_value: email },
          },
        ],
      },
      useDefaultData: false,
      useTriggerData: false,
      useUserData: false,
      updateTriggerData: false,
    })
  }

  const handleMoEngageTrackClickSubmit = () => {
    if (analyticsEventHandlerEnabled) {
      sendMoEngageEvent('search_perangkat_ajar')
    }
    trackClickSubmit()
  }

  const handleFilterSheetSubmit = () => {
    setIsUsingCachedToolkits(false)
    handleMoEngageTrackClickSubmit()

    // eslint-disable-next-line unused-imports/no-unused-vars
    const { filterSheetOpen, from, ...otherQuery } = query
    router.replace({
      pathname,
      query: {
        ...otherQuery,
      },
    })
  }

  const handleSubmit = () => {
    const submittedQuery = {
      pathname: '/toolkits',
      query: {
        ...query,
        ...(keyword && { title: keyword }),
      },
    }

    handleMoEngageTrackClickSubmit()

    if (asFilterSheet) router.replace(submittedQuery)
    else router.push(submittedQuery)
  }

  const isIncludeModulBahanOrBuku = (
    toolkitType: TGeneralToolkit | TGeneralToolkitSplitBahanAjar
  ) => ['modul_ajar', 'modul_bahan_ajar', 'buku'].includes(toolkitType)

  React.useEffect(() => {
    const lastSelectedToolkitType =
      toolkitTypeToGeneralToolkitMapping[toolkitType]
    if (activeToolkitType === lastSelectedToolkitType) return

    const cachedSubject = isIncludeModulBahanOrBuku(activeToolkitType)
      ? cachedModulBahanBukuSubject
      : cachedModulProjekSubject

    const isPersistSubject =
      isIncludeModulBahanOrBuku(lastSelectedToolkitType) &&
      isIncludeModulBahanOrBuku(activeToolkitType)

    if (!isInitialized.current) isInitialized.current = true

    const query = {}
    query['toolkit_type'] =
      generalToolkitToToolkitTypeMapping[activeToolkitType]

    if (phase || cachePhase) query['phase'] = phase || cachePhase

    if (phaseGroup || cachePhaseGroup)
      query['phaseGroup'] = phaseGroup || cachePhaseGroup

    if ((isPersistSubject && !!subject) || !!cachedSubject)
      query['subject'] = (isPersistSubject && subject) || cachedSubject

    if (isActiveToolkitTypeModulOrBahanAjar && !!cacheTitle)
      query['title'] = cacheTitle

    if (
      isActiveToolkitTypesContainModulProjek &&
      !!cachedModulProjekThemeKey &&
      isUseV2Theme
    )
      query['theme_key'] = cachedModulProjekThemeKey

    if (asFilterSheet) query['filterSheetOpen'] = true

    // Kinda hate this conditional since default value is null instead of array, and it is also
    // the same with others above. I wish it can be typed better to reduce confusion,
    // but it kinda requires another language. Sigh.
    if (videosFilter || cacheVideosFilter)
      query['videos_filter'] = videosFilter || cacheVideosFilter

    // Reset phase and phaseGroup query
    // Context: Kesetaraan is not available in Modul Projek
    if (activeToolkitType === 'modul_projek' && phaseGroup === 'Kesetaraan') {
      delete query['phase']
      delete query['phaseGroup']
    }

    router.replace(
      {
        pathname,
        query,
      },
      undefined,
      { shallow: true }
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeToolkitType])

  React.useEffect(() => {
    if (isInitialized.current) {
      const isToolkitModulBahanOrBuku = isIncludeModulBahanOrBuku(
        toolkitTypeToGeneralToolkitMapping[toolkitType]
      )
      if (phase) setPhase(phase)
      if (phaseGroup) setPhaseGroup(phaseGroup)
      if (JSON.stringify(phase) !== JSON.stringify(cachePhase)) {
        setModulBahanBukuSubject('')
        setModulProjekSubject('')
        setModulProjekThemeKey('')
      } else {
        if (isToolkitModulBahanOrBuku) setModulBahanBukuSubject(subject)
        if (!isToolkitModulBahanOrBuku) setModulProjekSubject(subject)
        if (!isToolkitModulBahanOrBuku && themeKey) {
          setModulProjekThemeKey(themeKey)
        }
      }
      if (videosFilter) setVideosFilter(videosFilter)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  React.useEffect(() => {
    const isKeywordDiffer = (queryKeyword || '') !== (keyword || '')

    if (isInitialized.current && (isKeywordDiffer || keyword === '')) {
      clearTimeout(timeout.current)

      timeout.current = setTimeout(() => {
        // eslint-disable-next-line unused-imports/no-unused-vars
        const { title, ...otherQuery } = qs.parse(window.location.search)
        if (title !== keyword) {
          const query = { ...otherQuery }

          if (!!keyword && isActiveToolkitTypeModulOrBahanAjar)
            query['title'] = keyword

          if (asFilterSheet) query['filterSheetOpen'] = 'true'

          setTitle(keyword)
          router.replace({
            pathname,
            query,
          })
        }
      }, 200)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword])

  React.useEffect(() => {
    if (!isInitialized.current) return

    setIsKeywordActive(isActiveToolkitTypeModulAjar && !!cacheTitle)
    setKeyword(isActiveToolkitTypeModulOrBahanAjar ? cacheTitle || '' : '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeToolkitType, cacheTitle])

  return (
    <main className="w-full bg-white px-4 pb-4">
      {isBannerSurveyShowed && <BannerSurvey />}
      <Text variant="heading-lg" className="my-4">
        {textSearchTitle}
      </Text>
      <div
        className={clsx(
          'mb-3 flex gap-x-2 overflow-x-auto whitespace-nowrap',
          styles.badgeContainer
        )}
      >
        {generalToolkitList.map(({ label, name }) => (
          <Badge
            key={name}
            className={clsx(
              'inline-block cursor-pointer border !py-2 !px-3',
              activeToolkitType === name
                ? '!border-interactive-default !bg-surface-informational-subdued'
                : 'text-default !border-[#808489]'
            )}
            onClick={() => onToolkitTypeClicked(name)}
          >
            <Text
              className={clsx(
                '!font-semibold',
                activeToolkitType === name ? '!text-interactive-default' : ''
              )}
            >
              {label}
            </Text>
          </Badge>
        ))}
      </div>

      {/* Other than Bahan Ajar, but if experiment === alt-2 then even if Bahan Ajar it still shows */}
      {(!isActiveToolkitTypeBahanAjar || !isExperimentAlt2) && (
        <section className="mb-5 flex flex-col gap-y-4">
          <SelectedPhase
            handleFilterChange={handleFilterChange}
            phase={phase}
            phaseGroup={phaseGroup}
          />

          <SelectedSubject
            handleFilterChange={handleFilterChange}
            toolkitType={toolkitType}
            selectedSubject={selectedSubject}
            phase={phase}
            activeToolkitType={activeToolkitType}
          />
        </section>
      )}

      {/* Only Bahan Ajar and experiment === alt-2 */}
      {isActiveToolkitTypeBahanAjar && isExperimentAlt2 && (
        <section className="flex flex-col">
          <KeywordInput
            ref={inputRef}
            keyword={keyword}
            setKeyword={setKeyword}
            setIsKeywordActive={setIsKeywordActive}
            isActiveToolkitTypeBahanAjar={isActiveToolkitTypeBahanAjar}
          />
        </section>
      )}

      {/* Always show when Bahan Ajar */}
      {isActiveToolkitTypeBahanAjar && (
        <section className="mt-4">
          <CheckboxGroup
            name="videosFilter"
            value={selectedVideosFilter}
            onChange={setSelectedVideosFilter}
          >
            <Checkbox value="video" className="!items-center">
              Video
            </Checkbox>
            <Checkbox value="non-video" className="ml-3 !items-center">
              Non Video
            </Checkbox>
          </CheckboxGroup>
        </section>
      )}

      {isActiveToolkitTypeModulAjar && !isKeywordActive && !keyword && (
        <ShowKeywordButton
          setIsKeywordActive={setIsKeywordActive}
          subject={subject}
        />
      )}
      {isKeywordActive && (
        <KeywordInput
          ref={inputRef}
          keyword={keyword}
          setKeyword={setKeyword}
          setIsKeywordActive={setIsKeywordActive}
        />
      )}

      <Button
        onClick={asFilterSheet ? handleFilterSheetSubmit : handleSubmit}
        disabled={isButtonDisabled}
        className="mt-4 w-full"
      >
        {textSearchActionButton}
      </Button>
    </main>
  )
}

export default SearchSection
